import React, { useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { decode } from "he";
import { InputPassword, Button, InputPasswordProps } from "ii-ui-kit";
import { FieldErrors, FieldNamesMarkedBoolean, useForm } from "react-hook-form";
import { SubmitHandler } from "react-hook-form/dist/types/form";

import { updatePasswordFormSchema } from "consts";
import { clearFormError } from "utils/clear-form-error/clear-form-error";
import { postForm } from "utils/post-form/post-form";

import "./login-update-password-page.scss";

export interface IUpdatePasswordFormFields {
  password: string;
  retryPassword: string;
}

/** Страница обновления пароля */
export const LoginUpdatePasswordPage = () => {
  /** Форма отправляется ? */
  const [isFormSending, setIsFormSending] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, submitCount },
  } = useForm<IUpdatePasswordFormFields>({
    mode: "onSubmit",
    resolver: yupResolver(updatePasswordFormSchema),
  });

  const onSubmit: SubmitHandler<IUpdatePasswordFormFields> = (data) => {
    if (!window.loginAction) {
      return;
    }

    setIsFormSending(true);
    clearFormError();
    postForm(window.loginAction ? decode(window.loginAction) : "", {
      username: window.username!,
      password: "",
      "password-new": data.password,
      "password-confirm": data.retryPassword,
    });
  };

  const getValidationMetaData = (
    fieldName: keyof IUpdatePasswordFormFields,
    errors: FieldErrors<IUpdatePasswordFormFields>,
    dirtyFields: FieldNamesMarkedBoolean<IUpdatePasswordFormFields>
  ): Pick<
    InputPasswordProps,
    "validationStatus" | "errorText" | "successText"
  > => {
    const blankValidationData: Pick<
      InputPasswordProps,
      "validationStatus" | "errorText" | "successText"
    > = {
      validationStatus: undefined,
      errorText: undefined,
      successText: undefined,
    };

    if (!submitCount) {
      return blankValidationData;
    }

    if (errors[fieldName]) {
      return {
        ...blankValidationData,
        validationStatus: "error",
        errorText: errors[fieldName]?.message,
      };
    }

    if (dirtyFields[fieldName]) {
      return {
        ...blankValidationData,
        validationStatus: "success",
        successText: "Пароль соответствует требованиям безопасности",
      };
    }

    return blankValidationData;
  };

  return (
    <form
      className={"login-update-password-page"}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="login-update-password-page__title">
        Создание пароля для личного кабинета
      </div>
      <div className="login-update-password-page__text">
        Длина пароля не менее 10 символов. Обязательны буквы A-Z, a-z, цифры и
        специальные символы (!,@,# и т.д.).
      </div>
      <div className="login-update-password-page__inputs">
        <InputPassword
          autoComplete={"new-password"}
          addContainerClass={"login-update-password-page__input"}
          id={"password"}
          label={"Новый пароль"}
          placeholder={"Новый пароль"}
          form={"formField"}
          {...getValidationMetaData("password", errors, dirtyFields)}
          {...register("password")}
        />
        <InputPassword
          addContainerClass={"login-update-password-page__input"}
          id={"retryPassword"}
          label={"Подтвердите пароль"}
          placeholder={"Подтвердите пароль"}
          form={"formField"}
          {...getValidationMetaData("retryPassword", errors, dirtyFields)}
          {...register("retryPassword")}
        />
      </div>
      <Button
        isLoading={isFormSending}
        className={"login-update-password-page__button"}
        size={"m"}
        title={"Создать пароль"}
      />
    </form>
  );
};
